<script setup lang="ts">
import TemplateDetailModal from './TemplateDetailModal.vue'

import { Plus } from 'lucide-vue-next'

import type { CampaignType, TemplateData, TemplateModule } from '@/types/types'

const router = useRouter()
const route = useRoute()

const props = defineProps<{
  module: TemplateModule
  templates: TemplateData[]
  selectedTemplate?: TemplateData
  urlParams?: Record<string, string>
  isOpen: boolean
}>()

const emit = defineEmits<{ (e: 'onCloseModal'): void }>()

const { t } = useI18n()
const { applyTemplate, templatesByModule } = useTemplate()
const { send } = useEvent()

const isProcessing = ref(false)
const templateDetail = ref<typeof TemplateDetailModal>()
const currentTemplate = ref<TemplateData>()
const selectTemplate = (template?: TemplateData) => {
  currentTemplate.value = template
  if (currentTemplate.value) {
    send('segment', `${props.module}.template:detail:clicked`, {
      templateId: template?.id,
      module: props.module
    })
  }
}

const computedModule = computed(() => {
  return props.module === 'legacy-contests' ? 'contests' : props.module
})

const submodule = computed<CampaignType | null>(() => (route.query.type as CampaignType) || null)

watch(
  () => props.selectedTemplate,
  (newTemplate) => {
    selectTemplate(newTemplate)
  }
)

watch(
  () => props.urlParams,
  () => {
    router.push({ query: props.urlParams })
  }
)
selectTemplate(props.selectedTemplate)

if (route.query.templateFromSuccessPage) {
  const routeTemplate = props.templates.find((template) => template.id === Number(route.query.templateFromSuccessPage))
  if (routeTemplate) {
    selectTemplate(routeTemplate)
  }
}

const handleClose = () => {
  if (currentTemplate.value) {
    currentTemplate.value = null
  } else {
    emit('onCloseModal')
  }
}

const handleCreate = () => {
  applyTemplate(props.module, props.urlParams)
}

const texts = computed(() => {
  const submoduleKey = `pages.${computedModule.value}${submodule?.value ? `.${submodule.value}` : ''}`
  return {
    title: t(`${submoduleKey}.modal.title`),
    buttonTitle: t(`${submoduleKey}.modal.buttonTitle`),
    template: t(`pages.${computedModule.value}.modal.template`)
  }
})

const submitTemplate = async () => {
  isProcessing.value = true
  await templateDetail.value.handleRedirect()
  isProcessing.value = false
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="texts.title"
    :link-text="currentTemplate && t(`pages.${computedModule}.modal.create.template.linkText`)"
    :size="templates.length > 4 ? '6xl' : '4xl'"
    :cancel="currentTemplate && t('global.words.previous')"
    :is-processing="isProcessing"
    @click="submitTemplate"
    @close="handleClose"
  >
    <div v-if="!currentTemplate" class="flex flex-col">
      <div class="flex w-full flex-1 flex-col items-start overflow-auto break-words text-left">
        <div>
          <p class="font-semibold text-foreground">
            {{ texts.buttonTitle }}
          </p>
          <Button variant="outline" class="mt-3" @click="handleCreate">
            <Plus />
            {{ texts.title }}
          </Button>
        </div>
        <div class="my-6 h-full w-full">
          <p class="font-semibold text-foreground">
            {{ texts.template }}
          </p>
          <div class="mt-3 grid w-full auto-rows-fr grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <BaseTemplateCard
              v-for="template in templates"
              :key="template.id"
              :title="template.name"
              :icon="templatesByModule[computedModule].icon"
              :button-text="t(`pages.${computedModule}.templates.cards.button`)"
              @click="selectTemplate(template)"
              @action="selectTemplate(template)"
            />
          </div>
        </div>
      </div>
    </div>
    <TemplateDetailModal
      v-else
      ref="templateDetail"
      :template="currentTemplate"
      :module="module"
      @previous="selectTemplate()"
    />
  </BaseModal>
</template>
